import { useEffect, useState } from 'react';

import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { SplashScreen } from '@tankef/ui';

import { useClientIsAuthenticated, useClientIsLoading } from '@/domains/identity/auth/api';

let guestInitialCheckCompleted = false;

/**
 * Rutas de CONTINUACIÓN del expediente, alcanzables CON sesión iniciada.
 *
 * `GuestRoute` existe para no mostrar login ni registro a quien ya entró. Pero
 * bajo su subárbol también viven `/onboarding/*` y `/kyc/*`, que son justo
 * donde tiene que aterrizar alguien que ya tiene cuenta y le falta terminar su
 * expediente: verificar identidad, firmar contratos, o retomar donde se quedó.
 *
 * Sin esta excepción, `isAuthenticated` —que se pone en true en cuanto hay
 * token— rebotaba a `/dashboard` a todo el que pulsara:
 *
 *   - los dos trámites de la sección "Trámites pendientes" del perfil
 *   - los atajos del menú de perfil en el header
 *   - el cintillo del dashboard, cuyo `next_action.href` es `/onboarding`
 *
 * Los tres navegaban y el guard los devolvía al dashboard, así que parecía que
 * el botón "no hacía nada". Ninguno funcionó nunca, ni para persona física ni
 * para moral.
 *
 * Las páginas destino resuelven por sí mismas el estado real —`OnboardingEntry`
 * consulta `next_step` y redirige— así que a quien ya terminó su expediente no
 * se le queda una pantalla sin sentido delante.
 */
const CONTINUACION_DE_EXPEDIENTE = ['/onboarding', '/kyc'] as const;

function esContinuacionDeExpediente(pathname: string): boolean {
  return CONTINUACION_DE_EXPEDIENTE.some(
    base => pathname === base || pathname.startsWith(`${base}/`)
  );
}

export function GuestRoute() {
  const isAuthenticated = useClientIsAuthenticated();
  const isLoading = useClientIsLoading();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(!guestInitialCheckCompleted);

  useEffect(() => {
    if (!isLoading && isChecking) {
      const timer = setTimeout(() => {
        setIsChecking(false);
        guestInitialCheckCompleted = true;
      }, 500);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isLoading, isChecking]);

  if (isChecking) {
    return <SplashScreen />;
  }

  if (isAuthenticated && !esContinuacionDeExpediente(location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
